<umkt-card-wrapper
  [title]="'pages.companyCard.title' | i18n"
  [icon]="{ name: 'empresa', type: 'mondrian-icons' }"
  [showCheck]="isFormValid"
  [loading]="(isLoading$ | async) ?? false"
  [initiallyOpen]="true"
>
  <section class="company-card company-data-container">
    <div *ngIf="userInformation$ | async as userInformation">
      <dl class="grid">
        <div class="field">
          <dt>{{ 'pages.companyCard.cnpj' | i18n }}</dt>
          <dd *ngIf="userInformation?.document">{{ formatCnpj(userInformation.document) }}</dd>
        </div>

        <div class="field">
          <dt>{{ 'pages.companyCard.businessName' | i18n }}</dt>
          <dd *ngIf="userInformation?.business">{{ userInformation.business }}</dd>
        </div>

        <div class="field">
          <dt>{{ 'pages.companyCard.tradeName' | i18n }}</dt>
          <dd *ngIf="userInformation?.displayName">{{ userInformation.displayName }}</dd>
        </div>

        <div class="field" *ngIf="!enableMindCustomerFields || !isEditing">
          <dt>{{ 'pages.companyCard.email' | i18n }}</dt>
          <dd *ngIf="enableMindCustomerFields && displayMindCustomer?.email; else emailFromUser">
            {{ displayMindCustomer?.email }}
          </dd>
          <ng-template #emailFromUser>
            <dd *ngIf="userInformation?.email">{{ userInformation.email }}</dd>
          </ng-template>
        </div>

        <div class="field" *ngIf="!enableMindCustomerFields || !isEditing">
          <dt>{{ 'pages.companyCard.responsible' | i18n }}</dt>
          <dd *ngIf="enableMindCustomerFields && displayMindCustomer?.contactName; else responsibleFromUser">
            {{ displayMindCustomer?.contactName }}
          </dd>
          <ng-template #responsibleFromUser>
            <dd>
              {{
                userInformation?.firstName || userInformation?.lastName
                  ? (userInformation.firstName + ' ' + userInformation.lastName).trim()
                  : userInformation?.displayName
              }}
            </dd>
          </ng-template>
        </div>

        <div
          class="field"
          *ngIf="enableMindCustomerFields && (!isEditing || displayMindCustomer?.cpf)"
        >
          <dt>{{ 'pages.companyCard.responsibleCpf' | i18n }}</dt>
          <dd *ngIf="displayMindCustomer?.cpf">{{ displayMindCustomer?.cpf }}</dd>
        </div>

        <div class="field" *ngIf="!enableMindCustomerFields || !isEditing">
          <dt>{{ 'pages.companyCard.companyPhone' | i18n }}</dt>
          <dd *ngIf="enableMindCustomerFields; else phoneFromUser">
            {{ displayCompanyPhone }}
          </dd>
          <ng-template #phoneFromUser>
            <dd *ngIf="userInformation?.phone">{{ formatPhone(userInformation.phone) }}</dd>
          </ng-template>
        </div>

        <div class="field" *ngIf="!enableMindCustomerFields">
          <dt>{{ 'pages.companyCard.phone' | i18n }}</dt>
          <dd *ngIf="userInformation?.phone">{{ formatPhone(userInformation.phone) }}</dd>
        </div>

        <div class="field field--full">
          <dt>{{ 'pages.companyCard.address' | i18n }}</dt>
          <dd *ngIf="getPrimaryAddress(userInformation) as addr">
            {{ addr.address }}
            <ng-container *ngIf="addr.number">, {{ addr.number }}</ng-container>
            <ng-container *ngIf="addr.neighborhood"> - {{ addr.neighborhood }}</ng-container>
            <ng-container *ngIf="addr.city && addr.state"> - {{ addr.city }}/{{ addr.state }}</ng-container>
            <ng-container *ngIf="addr.zipCode"> - {{ 'pages.companyCard.zipCode' | i18n }}: {{ formatZipCode(addr.zipCode) }}</ng-container>
            <ng-container *ngIf="addr.complement"> - {{ addr.complement }}</ng-container>
          </dd>
        </div>
      </dl>

      <form
        class="mind-customer-form"
        *ngIf="enableMindCustomerFields && isEditing"
        [formGroup]="mindForm"
      >
        <h3 class="mind-customer-form__title">
          {{ 'pages.companyCard.mindSectionTitle' | i18n }}
        </h3>

        <div class="mind-customer-form__grid">
          <div class="mind-field">
            <umkt-custom-input
              formControlName="contactName"
              id="mind-contactName"
              [placeholder]="('pages.companyCard.responsible' | i18n) + ' *'"
              [maxlength]="255"
              [error]="!!(mindForm.get('contactName')?.invalid && mindForm.get('contactName')?.touched)"
            ></umkt-custom-input>
          </div>

          <div class="mind-field">
            <umkt-custom-input
              formControlName="cpf"
              id="mind-cpf"
              [placeholder]="('pages.companyCard.responsibleCpf' | i18n) + ' *'"
              mask="000.000.000-00"
              [error]="!!(mindForm.get('cpf')?.invalid && mindForm.get('cpf')?.touched)"
            ></umkt-custom-input>
          </div>

          <div class="mind-field">
            <umkt-custom-input
              formControlName="companyPhone"
              id="mind-companyPhone"
              [placeholder]="'pages.companyCard.companyPhone' | i18n"
              mask="(00) 0000-0000||(00) 00000-0000"
            ></umkt-custom-input>
            <small class="mind-field__hint">
              {{ 'pages.companyCard.companyPhoneHint' | i18n }}
            </small>
          </div>

          <div class="mind-field">
            <umkt-custom-input
              formControlName="email"
              id="mind-email"
              type="email"
              [placeholder]="('pages.companyCard.email' | i18n) + ' *'"
              [maxlength]="255"
              [error]="!!(mindForm.get('email')?.invalid && mindForm.get('email')?.touched)"
            ></umkt-custom-input>
          </div>
        </div>

        <div class="mind-marketing">
          <p class="mind-marketing__title">{{ 'pages.companyCard.marketingTitle' | i18n }}</p>
          <label class="mind-marketing__item">
            <input type="checkbox" formControlName="marketingSms" />
            <span>{{ 'pages.companyCard.marketingSms' | i18n }}</span>
          </label>
          <label class="mind-marketing__item">
            <input type="checkbox" formControlName="marketingCall" />
            <span>{{ 'pages.companyCard.marketingCall' | i18n }}</span>
          </label>
          <label class="mind-marketing__item">
            <input type="checkbox" formControlName="marketingVoice" />
            <span>{{ 'pages.companyCard.marketingVoice' | i18n }}</span>
          </label>
          <label class="mind-marketing__item">
            <input type="checkbox" formControlName="marketingEmail" />
            <span>{{ 'pages.companyCard.marketingEmail' | i18n }}</span>
          </label>
        </div>
      </form>
    </div>

    <div *ngIf="(userInformation$ | async) === null" class="no-user-message">
      <p>{{ 'pages.companyCard.noData' | i18n }}</p>
    </div>

    <div class="company-card-actions" *ngIf="isEditing">
      <button
        class="btn-primary"
        type="button"
        (click)="onContinue()"
        [disabled]="!isFormValid"
      >
        {{ 'shared.buttons.continue' | i18n }}
      </button>
    </div>
  </section>
</umkt-card-wrapper>
